<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px; font-size: small;">
      <div class="card card-sm">
        <div class="card-header" style="background-color: #DBECDB;">
          <h5 class="card-title">Gestión de Recursos</h5>
          <div class="row mb-3">
            <div class="col-md-8">
              <div class="form-group">
                <div class="input-group">
                  <input
                    type="text"
                    v-model="filters.search"
                    class="form-control"
                    placeholder="Buscar por descripción, fuente, programa o actividad..."
                    @keyup.enter="handleSearch"
                  />
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="handleSearch"
                  >
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="col-md-4 text-end">
              <BButton
                v-if="hasPermission('param_create')"
                @click="showModal(null)"
                variant="primary"
                class="btn-round me-2"
              >
                <i class="fa fa-plus me-1"></i> Nuevo Recurso
              </BButton>
              <BButton
                v-if="hasPermission('param_create')"
                variant="danger"
                class="btn-round"
                @click="generatePDF"
              >
                <i class="fa fa-file-pdf me-1"></i> PDF
              </BButton>
            </div>
          </div>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
          </div>

          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered">
                <thead class="text-primary">
                  <tr style="line-height: 8px; font-size: 0.8rem;">
                    <th>ID</th>
                    <th>Fuente</th>
                    <th>Programa</th>
                    <th>Actividad</th>
                    <th>Gestión</th>
                    <th class="text-end">Prog(Bs)</th>
                    <th class="text-end">Ejec(Bs)</th>
                    <th class="text-end">Saldo(Bs)</th>
                    <th>Descripción</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="recurso in recursos" :key="recurso.id">
                    <td>{{ recurso.id }}</td>
                    <td>
                      <span v-if="recurso.fuente_financiamiento">
                        {{ recurso.fuente_codigo }} - {{ recurso.fuente_nombre }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="recurso.programa_poa">
                        {{ recurso.programa_codigo }} - {{ recurso.programa_nombre }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="recurso.actividad_poa">
                        {{ recurso.actividad_codigo }} - {{ recurso.actividad_nombre }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>{{ recurso.gestion }}</td>
                    <td class="text-end">{{ formatMonto(recurso.monto_programado) }}</td>
                    <td class="text-end">{{ formatMonto(recurso.monto_ejecutado) }}</td>
                    <td class="text-end" :class="getSaldoClass(recurso.monto_saldo)">
                    {{ formatMonto(recurso.monto_saldo) }}
                    </td>
                    <td>{{ recurso.descripcion_recursos }}</td>
                    <td>
                      <span :class="getEstadoBadgeClass(recurso.estado)">
                        {{ recurso.estado }}
                      </span>
                    </td>
                    <td>
                      <div class="btn-group">
                        <BButton
                          v-if="hasPermission('param_update')"
                          @click="showModal(recurso)"
                          variant="warning"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-edit"></i>
                        </BButton>
                        <BButton
                          v-if="hasPermission('param_delete')"
                          @click="confirmDelete(recurso)"
                          variant="danger"
                          size="sm"
                        >
                          <i class="fa fa-trash"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Paginación -->
            <div class="row mt-3" v-if="pagination.total > 0">
              <div class="col-md-12">
                <BPagination
                  v-model="pagination.current_page"
                  :total-rows="pagination.total"
                  :per-page="pagination.per_page"
                  @update:modelValue="handlePageChange"
                  align="center"
                ></BPagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar -->
    <BModal
      v-model="modalVisible"
      :title="modalTitle"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Fuente Financiamiento</label>
              <select
                v-model="form.fuente_financiamiento"
                class="form-select"
                :class="{ 'is-invalid': errors.fuente_financiamiento }"
              >
                <option value="">Seleccione una fuente</option>
                <option
                  v-for="fuente in fuentesFinanciamiento"
                  :key="fuente.codigo"
                  :value="String(fuente.codigo)"
                >
                  {{ String(fuente.codigo) }} - {{ fuente.fuente_financiamiento }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.fuente_financiamiento">
                {{ errors.fuente_financiamiento[0] }}
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Programa POA</label>
              <select
                v-model="form.programa_poa"
                class="form-select"
                :class="{ 'is-invalid': errors.programa_poa }"
              >
                <option value="">Seleccione un programa</option>
                <option
                  v-for="programa in programasPoa"
                  :key="programa.codigo"
                  :value="String(programa.codigo)"
                >
                  {{ String(programa.codigo) }} - {{ programa.programa_poa }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.programa_poa">
                {{ errors.programa_poa[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Actividad POA</label>
              <select
                v-model="form.actividad_poa"
                class="form-select"
                :class="{ 'is-invalid': errors.actividad_poa }"
              >
                <option value="">Seleccione una actividad</option>
                <option
                  v-for="actividad in actividadesPoa"
                  :key="actividad.codigo"
                  :value="String(actividad.codigo)"
                >
                  {{ String(actividad.codigo) }} - {{ actividad.actividad_poa }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.actividad_poa">
                {{ errors.actividad_poa[0] }}
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-label">Gestión</label>
              <input
                type="number"
                v-model="form.gestion"
                class="form-control"
                :class="{ 'is-invalid': errors.gestion }"
                required
              />
              <div class="invalid-feedback" v-if="errors.gestion">
                {{ errors.gestion[0] }}
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-label">Monto (Bs)</label>
              <input
                type="number"
                step="0.01"
                v-model="form.monto"
                class="form-control"
                :class="{ 'is-invalid': errors.monto }"
                required
              />
              <div class="invalid-feedback" v-if="errors.monto">
                {{ errors.monto[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-8">
            <div class="form-group">
              <label class="form-label">Descripción del Recurso</label>
              <input
                type="text"
                v-model="form.descripcion_recursos"
                class="form-control"
                :class="{ 'is-invalid': errors.descripcion_recursos }"
                required
                maxlength="100"
              />
              <div class="invalid-feedback" v-if="errors.descripcion_recursos">
                {{ errors.descripcion_recursos[0] }}
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label class="form-label">Estado</label>
              <select
                v-model="form.estado"
                class="form-select"
                :class="{ 'is-invalid': errors.estado }"
                required
              >
                <option value="BORRADOR">BORRADOR</option>
                <option value="APROBADO">APROBADO</option>
                <option value="RECHAZADO">RECHAZADO</option>
              </select>
              <div class="invalid-feedback" v-if="errors.estado">
                {{ errors.estado[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer mt-3">
          <BButton
            type="button"
            variant="secondary"
            @click="modalVisible = false"
            class="me-2"
          >
            Cancelar
          </BButton>
          <BButton
            type="submit"
            variant="primary"
            :disabled="loadingForm"
          >
            <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </BButton>
        </div>
      </form>
    </BModal>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useAuthStore } from '../../stores/authStore';
import Swal from 'sweetalert2';
import { BModal, BButton, BPagination } from 'bootstrap-vue-3';

interface FuenteFinanciamiento {
  codigo: string;
  fuente_financiamiento: string;
}

interface ProgramaPoa {
  codigo: string;
  programa_poa: string;
}

interface ActividadPoa {
  codigo: string;
  actividad_poa: string;
}

interface Recurso {
  id: number;
  fuente_financiamiento: string | null;
  programa_poa: string | null;
  actividad_poa: string | null;
  gestion: number;
  monto: number;
  descripcion_recursos: string;
  estado: string;
  usuario_registro: string;
  fecha_registro: string;
  usuario_edicion: string | null;
  fecha_edicion: string | null;
  fuente_codigo?: string;
  fuente_nombre?: string;
  programa_codigo?: string;
  programa_nombre?: string;
  actividad_codigo?: string;
  actividad_nombre?: string;
  monto_programado: number;
  monto_ejecutado: number;
  monto_saldo: number;
}

interface Filters {
  search: string;
}

interface FormData {
  id?: number;
  fuente_financiamiento: string | null;
  programa_poa: string | null;
  actividad_poa: string | null;
  gestion: number;
  monto: number;
  descripcion_recursos: string;
  estado: string;
}

interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export default {
  components: {
    BModal,
    BButton,
    BPagination
  },
  setup() {
    const recursos = ref<Recurso[]>([]);
    const fuentesFinanciamiento = ref<FuenteFinanciamiento[]>([]);
    const programasPoa = ref<ProgramaPoa[]>([]);
    const actividadesPoa = ref<ActividadPoa[]>([]);
    const loading = ref(true);
    const authStore = useAuthStore();
    const filters = ref<Filters>({
      search: ''
    });

    // Variables para el modal
    const modalVisible = ref(false);
    const isEditing = ref(false);
    const loadingForm = ref(false);
    const errors = ref<Record<string, string[]>>({});

    const form = ref<FormData>({
      fuente_financiamiento: '',
      programa_poa: '',
      actividad_poa: '',
      gestion: new Date().getFullYear(),
      monto: 0,
      descripcion_recursos: '',
      estado: 'BORRADOR'
    });
    const getSaldoClass = (saldo: number) => {
        if (saldo < 0) return 'text-danger fw-bold';   // sobregirado
        if (saldo === 0) return 'text-muted';          // totalmente ejecutado
        return 'text-success fw-bold';                 // con saldo disponible
    };
    // Paginación
    const pagination = ref<Pagination>({
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1
    });

    const modalTitle = computed(() => {
      return isEditing.value ? 'Editar Recurso' : 'Nuevo Recurso';
    });

    const showModal = (recurso: Recurso | null) => {
      errors.value = {};
      if (recurso) {
        // Edición
        isEditing.value = true;
        form.value = {
          id: recurso.id,
          fuente_financiamiento: recurso.fuente_financiamiento,
          programa_poa: recurso.programa_poa,
          actividad_poa: recurso.actividad_poa,
          gestion: recurso.gestion,
          monto: recurso.monto,
          descripcion_recursos: recurso.descripcion_recursos,
          estado: recurso.estado
        };
      } else {
        // Nuevo
        isEditing.value = false;
        form.value = {
          fuente_financiamiento: '',
          programa_poa: '',
          actividad_poa: '',
          gestion: new Date().getFullYear(),
          monto: 0,
          descripcion_recursos: '',
          estado: 'BORRADOR'
        };
      }
      modalVisible.value = true;
    };

    const handleSubmit = async () => {
      loadingForm.value = true;
      errors.value = {};

      try {
        let response;

        if (isEditing.value) {
          response = await axios.post(`/recursos/${form.value.id}/update`, form.value);
        } else {
          response = await axios.post('/recursos/save', form.value);
        }

        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: isEditing.value ? 'Recurso actualizado correctamente' : 'Recurso creado correctamente'
        });

        modalVisible.value = false;
        fetchRecursos();
      } catch (error: any) {
        if (error.response && error.response.status === 422) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Ocurrió un error al procesar la solicitud'
          });
        }
      } finally {
        loadingForm.value = false;
      }
    };

    const fetchCatalogos = async () => {
      try {
        const [fuentes, programas, actividades] = await Promise.all([
          axios.get('/fuente-financiamiento/list-all'),
          axios.get('/programa-poa/list-all'),
          axios.get('/actividad-poa/list-all')
        ]);

        fuentesFinanciamiento.value = fuentes.data.fuentes || [];
        programasPoa.value = programas.data.programas || [];
        actividadesPoa.value = actividades.data.actividades || [];
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
      }
    };

    const fetchRecursos = async () => {
      loading.value = true;
      try {
        const params: Record<string, any> = {
          page: pagination.value.current_page,
          per_page: pagination.value.per_page
        };

        if (filters.value.search) {
          params.search = filters.value.search;
        }

        const response = await axios.get('/recursos/list', { params });
        recursos.value = response.data.recursos.data;

        // Actualizar paginación
        pagination.value = {
          current_page: response.data.recursos.current_page,
          per_page: response.data.recursos.per_page,
          total: response.data.recursos.total,
          last_page: response.data.recursos.last_page
        };

      } catch (error) {
        console.error('Error al cargar recursos:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los recursos'
        });
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.value.current_page = 1;
      fetchRecursos();
    };

    const handlePageChange = (page: number) => {
      pagination.value.current_page = page;
      fetchRecursos();
    };

    const confirmDelete = (recurso: Recurso) => {
      Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar el recurso "${recurso.descripcion_recursos}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteRecurso(recurso.id);
        }
      });
    };

    const deleteRecurso = async (id: number) => {
      try {
        await axios.get(`/recursos/${id}/delete`);
        Swal.fire({
          icon: 'success',
          title: 'Recurso eliminado',
          text: 'El recurso ha sido eliminado correctamente'
        });
        await fetchRecursos();
      } catch (error: any) {
        console.error('Error al eliminar recurso:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo eliminar el recurso'
        });
      }
    };

    const generatePDF = () => {
      const params = filters.value.search ? { search: filters.value.search } : {};

      axios.get('/recursos/reporte', {
        params,
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }).then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reporte_recursos.pdf`);
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      }).catch(error => {
        console.error('Error al generar PDF:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el PDF'
        });
      });
    };

    const formatMonto = (monto: number) => {
      return new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(monto);
    };

    const getEstadoBadgeClass = (estado: string) => {
      switch (estado) {
        case 'APROBADO':
          return 'badge bg-success';
        case 'RECHAZADO':
          return 'badge bg-danger';
        default:
          return 'badge bg-warning text-dark';
      }
    };

    const hasPermission = (permission: string) => {
      return authStore.hasPermission(permission);
    };

    onMounted(() => {
      fetchCatalogos();
      fetchRecursos();
    });

    return {
      recursos,
      fuentesFinanciamiento,
      programasPoa,
      actividadesPoa,
      filters,
      loading,
      form,
      errors,
      loadingForm,
      isEditing,
      modalTitle,
      modalVisible,
      pagination,
      handleSearch,
      handlePageChange,
      confirmDelete,
      hasPermission,
      showModal,
      handleSubmit,
      generatePDF,
      formatMonto,
      getEstadoBadgeClass,
      getSaldoClass
    };
  }
};
</script>

<style scoped>
  thead th {
    background-color: #D84315;
    color:#ffffff;
    position: sticky;
    top: 0;
  }
  tbody td {
    margin: 0px;
    padding: 8px !important;
    vertical-align: middle;
  }
  .table-responsive {
    max-height: 600px;
    overflow-y: auto;
  }
  .btn-round {
    border-radius: 50px;
  }
  .badge {
    padding: 5px 10px;
    border-radius: 20px;
    font-size: 0.75rem;
  }
  .text-end {
    text-align: right;
  }
</style>
